import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Send } from 'lucide-react';
import clientAxios from '../api/clientAxios';
import loadingGIF from '../assets/images/productos/GIF/GIF.gif'

const Contact = ({ contactParams }) => {
  const [formData, setFormData] = useState({
    nombre: '',
    empresa: '',
    email: '',
    telefono: '',
    tipoConsulta: '',
    consulta: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [formSuccess, setFormSuccess] = useState("");
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (contactParams && contactParams.tipoConsulta) {
      setFormData(prev => ({ ...prev, tipoConsulta: contactParams.tipoConsulta }));
    }

    if (contactParams && contactParams.scrollToForm) {
      const formSection = document.getElementById('contact-form-section');
      const appHeader = document.getElementById('app-header-mobile-fixed'); 

      if (formSection) {
        setTimeout(() => {
          const headerHeight = appHeader ? appHeader.offsetHeight : 0;
          const formSectionTop = formSection.getBoundingClientRect().top + window.pageYOffset;
          const targetScrollPosition = formSectionTop - headerHeight - 16;

          window.scrollTo({
            top: targetScrollPosition,
            behavior: 'smooth'
          });
        }, 150);
      }
    }
  }, [contactParams]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    setFormSuccess("");
    setFormError("");

    // Validación de campos obligatorios antes de enviar
    const missingFields = [];
    if (!formData.nombre) missingFields.push('Nombre');
    if (!formData.email) missingFields.push('Email');
    if (!formData.tipoConsulta) missingFields.push('Tipo de consulta');
    if (!formData.consulta) missingFields.push('Mensaje');
    if (missingFields.length > 0) {
      setFormError('Por favor complete los siguientes campos obligatorios: ' + missingFields.join(', ') + '.');
      return;
    }

    setIsLoading(true);
    try {
      // Capitalizar tipoConsulta antes de enviar
      const tipoConsulta = formData.tipoConsulta;
      let tipoConsultaCapitalizado = tipoConsulta;
      if (tipoConsulta && typeof tipoConsulta === 'string') {
        const tipo = tipoConsulta.trim().toLowerCase();
        tipoConsultaCapitalizado = tipo.charAt(0).toUpperCase() + tipo.slice(1);
      }
      const dataToSend = { ...formData, tipoConsulta: tipoConsultaCapitalizado };
      await clientAxios.post('/contacto', dataToSend);
      setFormSuccess("¡Consulta enviada correctamente! Nos pondremos en contacto en menos de 48 horas.");
      setFormData({ nombre: '', empresa: '', email: '', telefono: '', tipoConsulta: '', consulta: '' });
    } catch (err) {
      if (err.response?.data?.error) {
        setFormError(err.response.data.error);
      } else if (err.message && err.message.includes('Network Error')) {
        setFormError('No se pudo conectar con el servidor. Intente más tarde.');
      } else {
        setFormError('Error al enviar la consulta.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const contactMethods = [
    {
      title: "Teléfono",
      description: "Disponibles de lunes a viernes de 8:00 a 18:00 hrs",
      icon: Phone,
      // details: "+52 (462) 621 8268",
      // action: "tel:+5214626218268"
    },
    {
      title: "Correo electrónico",
      description: "Respondemos todos los correos en menos de 24 horas",
      icon: Mail,
      details: "contacto@campofresco.com.mx",
      action: "mailto:contacto@campofresco.com.mx"
    },
    {
      title: "Ubicación",
      description: "Nos encontramos en Calera, Guanajuato",
      icon: MapPin,
      details: "Carretera Irapuato - Silao Km 45, La Calera, Guanajuato",
      action: "https://maps.google.com"
    }
  ];

  return (
    <div className="bg-gray-50">
            {/* CTA Section */}
      <div className="bg-green-900">
        <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 sm:py-16 lg:px-8 text-center">
          <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
            <span className="block text-green-200">Contactanos</span>
          </h2>
          <p className="mt-6 text-lg leading-6 text-green-100">
          En Campo Fresco valoramos la comunicación con nuestros clientes.
          Si desea entablar diálogo con nuestro equipo, conocer más sobre lo que ofrecemos o compartir 
          alguna inquietud, estamos a su disposición. Le responderemos con gusto y prontitud.
          </p>
          <div className="mt-8 flex justify-center">
            <div className="inline-flex rounded-md shadow">
              <a
                href="mailto:contacto@campofresco.com.mx"
                className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-green-700 bg-white hover:bg-green-50"
              >
                Enviar correo
              </a>
            </div>
            <div className="ml-3 inline-flex">
              <a
                href="tel:+5214626218268"
                className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-green-600 hover:bg-green-800"
              >
                Llamar ahora
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="lg:grid lg:grid-cols-3 lg:gap-8">
          {/* Left Column - Contact Info */}
          <div className="lg:col-span-1 mb-12 lg:mb-0">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Contacto</h2>
            
            <div className="space-y-6">
              {contactMethods.map((method, index) => (
                <div key={index} className="flex items-start">
                  <div className="flex-shrink-0 bg-green-100 rounded-md p-3">
                    <method.icon className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-gray-900">{method.title}</h3>
                    <p className="mt-1 text-gray-600">{method.description}</p>
                    <a 
                      href={method.action} 
                      className="mt-2 inline-flex items-center text-green-600 hover:text-green-800"
                    >
                      {method.details}
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column - Form */}
          <div id="contact-form-section" className="lg:col-span-2 bg-white shadow-lg rounded-lg p-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Solicite información</h2>
            <p className="text-gray-600 mb-4">
              Complete el formulario y nuestro equipo se pondrá en contacto con usted en menos de 48 horas.
            </p>

            <form className="space-y-4">
              <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="nombre" className="block text-sm font-medium text-gray-700">
                    Nombre completo *
                  </label>
                  <input
                    type="text"
                    name="nombre"
                    id="nombre"
                    value={formData.nombre}
                    onChange={handleInputChange}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-4 focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                <div>
                  <label htmlFor="empresa" className="block text-sm font-medium text-gray-700">
                    Empresa u organización
                  </label>
                  <input
                    type="text"
                    name="empresa"
                    id="empresa"
                    value={formData.empresa}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-4 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email de contacto *
                  </label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-4 focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                <div>
                  <label htmlFor="telefono" className="block text-sm font-medium text-gray-700">
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    name="telefono"
                    id="telefono"
                    value={formData.telefono}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-4 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="tipoConsulta" className="block text-sm font-medium text-gray-700">
                  Tipo de consulta *
                </label>
                <select
                  id="tipoConsulta"
                  name="tipoConsulta"
                  value={formData.tipoConsulta}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-4 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="">Seleccione una opción</option>
                  <option value="Compras">Compras</option>
                  <option value="Reclutamiento">Reclutamiento</option>
                  <option value="Ventas">Ventas</option>
                </select>
              </div>

              <div>
                <label htmlFor="consulta" className="block text-sm font-medium text-gray-700">
                  Mensaje *
                </label>
                <textarea
                  id="consulta"
                  name="consulta"
                  rows={3} 
                  value={formData.consulta}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-4 focus:ring-green-500 focus:border-green-500"
                  placeholder="Describa su proyecto, volúmenes requeridos o cualquier detalle relevante..."
                ></textarea>
              </div>

              <div>
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="w-full flex justify-center items-center py-3 px-6 border border-transparent rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  disabled={isLoading}
                >
                  <Send className="h-5 w-5 mr-2" />
                  {isLoading ? 'Enviando...' : 'Enviar mensaje'}
                </button>
                {isLoading && (
                  <img src={loadingGIF} alt="Cargando..." className="w-25 h-25 mx-auto mt-4" style={{ width: '100px', height: '100px' }} />
                )}
                {formSuccess && <div className="text-green-600 text-sm mt-2">{formSuccess}</div>}
                {formError && <div className="text-red-600 text-sm mt-2">{formError}</div>}
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Map Section */}
      <div className="bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Nuestra ubicación</h2>
          <div className="rounded-lg overflow-hidden shadow-lg">
            <iframe 
              src="https://maps.google.com/maps?q=Campo+Fresco,Carretera+Irapuato+Silao+km+45,La+Calera,Guanajuato,Mexico&t=m&z=16&output=embed"
              width="100%" 
              height="450" 
              style={{ border: 0 }}
              allowFullScreen="" 
              loading="lazy" 
              title="Campo Fresco Ubicación"
            ></iframe>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Contact;